import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, switchMap } from 'rxjs';

import { UserListApiService, UserModel } from '@ninety/mobile/platform/api';
import { DatadogLogsService } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';
import { SessionActions } from '../../app-global/session/session.actions';
import { SessionSelectors } from '../../app-global/session/session.selectors';
import { CompanyUsersStateActions } from '../company-users/company-users-state.actions';

import { UserListStateActions } from './user-list-state.actions';
import { CurrentUserSelectors } from './user-list-state.selectors';

@Injectable()
export class UserListStateEffects {
  private readonly action$ = inject(Actions);
  private readonly userListApi = inject(UserListApiService);
  private readonly store = inject(Store);
  private readonly logs = inject(DatadogLogsService);

  /**
   * Which company user the app is fetching as, read off the Access Token.
   *
   * Safe to read the slice here for the same reason as `CurrentPersonEffects`: `initGlobalState`
   * comes from the machine's `notifyAppSignedIn` Command, and the state advances before any Command
   * runs.
   */
  logInSuccess_setUserId$ = createEffect(() =>
    this.action$.pipe(
      ofType(appActions.initGlobalState),
      concatLatestFrom(() => this.store.select(SessionSelectors.accessTokenClaims)),
      map(([, claims]) => {
        if (!claims?.userId) {
          this.logs.error('No readable userId on the Access Token in UserListStateEffects');
          // `sessionUnusable` rather than `signOutRequested`, matching `CurrentPersonEffects` — see
          // there. Nobody asked to sign out, and `userRequested` is the one reason that is never a
          // defect, so it is the one this must not borrow.
          return SessionActions.eventReceived({
            event: { type: 'sessionUnusable', cause: { kind: 'unexpectedFailure' } },
          });
        }

        return UserListStateActions.setLoggedInUserId({ loggedInUserId: claims.userId });
      })
    )
  );

  initGlobalState_fetchUsers$ = createEffect(() =>
    this.action$.pipe(
      ofType(appActions.initGlobalState),
      map(() => UserListStateActions.fetch())
    )
  );

  companyUserSuccess_setLoggedInUser$ = createEffect(() =>
    this.action$.pipe(
      ofType(CompanyUsersStateActions.fetchSuccess),
      concatLatestFrom(() => this.store.select(CurrentUserSelectors.selectId)),
      map(([{ companyUsers }, id]) => companyUsers.find(user => user._id === id) as UserModel),
      filter(loggedInUser => !!loggedInUser),
      map(loggedInUser => UserListStateActions.setLoggedInUser({ loggedInUser }))
    )
  );

  $fetch = createEffect(() =>
    this.action$.pipe(
      ofType(UserListStateActions.fetch),
      switchMap(() =>
        this.userListApi.getUserList({}).pipe(
          map(userList => UserListStateActions.fetchSuccess({ users: userList })),
          catchError(error => of(UserListStateActions.fetchError({ error })))
        )
      )
    )
  );

  updateLoggedInUser$ = createEffect(() =>
    this.action$.pipe(
      ofType(UserListStateActions.updateLoggedInUser),
      concatLatestFrom(() => this.store.select(CurrentUserSelectors.selectId)),
      exhaustMap(([{ update }, userId]) =>
        this.userListApi.updateUser(userId, update).pipe(
          map(() => UserListStateActions.updateLoggedInUserSuccess({ update })),
          catchError(error => of(UserListStateActions.updateLoggedInUserError({ error })))
        )
      )
    )
  );
}
